import HiddenInput from "@/components/form/components/HiddenInput";
import LabelWithTooltip from "@/components/form/labels/LabelWithTooltip";
import { InputNumber } from "@/components/InputNumber";
import { datePickerFormItemProps, datePickerStandardProps, selectStandardProps } from "@/utils/componentUtils";
import { formatCoverageLimitValues, formatLocaleCurrency } from "@/utils/formatUtils";
import { licensePlateNormalizeFunction, upperCaseStringNormalizeFunction } from "@/utils/formUtils";
import { isDefined, toDate } from "@/utils/utils";
import { regexPatterns, validations } from "@/utils/validationUtils";
import { Card, Cascader, Checkbox, Col, DatePicker, Divider, Form, Input, Row, Select } from "antd";
import { DefaultOptionType } from "antd/es/select";
import dayjs, { Dayjs } from "dayjs";
import { useTranslation } from "react-i18next";
import { rowGutter } from "../../../../../../../common/constants";
import { ClientType } from "../../../../../../client/enums";
import { PaymentFrequency } from "../../../../../../contract/enums";
import CityAutoComplete from "../../../../../../enumerations/components/form/CityAutoComplete";
import { InstitutionEnum } from "../../../../../../institution/enums";
import { CalcType, ClientExperience } from "../../../../../enums";
import { CalcResult } from "../../../../types";
import { groupByInstitution, isApplicableSuccessResult } from "../../../../utils";
import { useVehicleCalcResults } from "../../../contexts";
import { GapCalcResultData, VehicleCalc, VehicleCalcResultData, VehicleGenGeneralData } from "../../../types";
import {
  getVehicleCalcResultsByType,
  isCrashCalcType,
  isGapCalcType,
  isMtplCalcType,
  isMtplCrashCalcType,
  resolveGapCoverageLimitOptions,
} from "../../../utils";

const colSpan = 4;
const bigColSpan = 8;

interface Props {
  policyHolderType?: ClientType;
  calcData: VehicleCalc;
  selectedResult: CalcResult<VehicleCalcResultData>;
}

export const VehicleGenOtherDataSection = ({ policyHolderType, calcData, selectedResult }: Props) => {
  const { t } = useTranslation();

  const { applicableResults } = useVehicleCalcResults();

  const { institutionEnum: selectedResultInstitutionEnum } = selectedResult.insuranceInstitution;
  const { calcType: selectedResultCalcType } = selectedResult;

  const getRecommendedResultOptions = (): DefaultOptionType[] => {
    if (isMtplCalcType(selectedResultCalcType)) {
      const filteredCalcResults = getVehicleCalcResultsByType(applicableResults, selectedResultCalcType).filter(
        isApplicableSuccessResult,
      );

      const groupedCalcResults = groupByInstitution(filteredCalcResults);

      return groupedCalcResults.map((institutionResults) => ({
        label: institutionResults[0]?.insuranceInstitution.name,
        value: institutionResults[0]?.insuranceInstitution.institutionEnum,
        children: institutionResults.map((result) => {
          const limit =
            result.data.healthCoverageLimit && result.data.propertyCoverageLimit
              ? formatCoverageLimitValues(result.data.healthCoverageLimit, result.data.propertyCoverageLimit)
              : undefined;

          const label = limit ? `${result.coverage} (${limit})` : result.coverage;

          return {
            label: label,
            value: result.coverage,
          };
        }),
      }));
    } else if (isMtplCrashCalcType(selectedResultCalcType)) {
      const filteredCalcResults = getVehicleCalcResultsByType(applicableResults, selectedResultCalcType).filter(
        (result) => isApplicableSuccessResult(result),
      );
      const groupedCalcResults = groupByInstitution(filteredCalcResults);

      return groupedCalcResults.map((institutionResults) => ({
        label: institutionResults[0]?.insuranceInstitution.name,
        value: institutionResults[0]?.insuranceInstitution.institutionEnum,
        children: institutionResults.map((result) => {
          const limit =
            result.data.healthCoverageLimit && result.data.propertyCoverageLimit
              ? formatCoverageLimitValues(result.data.healthCoverageLimit, result.data.propertyCoverageLimit)
              : undefined;

          const label: string[] = [];

          if (selectedResultInstitutionEnum === InstitutionEnum.KOOPERATIVA && result.coverage) {
            label.push(result.coverage);
          }

          if (result.data.complicity) {
            label.push(result.data.complicity);
          }

          if (limit) {
            label.push(limit);
          }

          return {
            label: label.join(" | "),
            value: result.coverage,
          };
        }),
      }));
    } else if (isCrashCalcType(selectedResultCalcType)) {
      const filteredCalcResults = getVehicleCalcResultsByType(applicableResults, selectedResultCalcType).filter(
        (result) => isApplicableSuccessResult(result),
      );
      const groupedCalcResults = groupByInstitution(filteredCalcResults);

      return groupedCalcResults.map((institutionResults) => ({
        label: institutionResults[0]?.insuranceInstitution.name,
        value: institutionResults[0]?.insuranceInstitution.institutionEnum,
        children: institutionResults.map((result) => ({
          label: `${t("calc.vehicle.results.complicity")} ${result.data?.complicity}`,
          value: result.coverage,
        })),
      }));
    } else if (isGapCalcType(selectedResultCalcType)) {
      const filteredCalcResults = getVehicleCalcResultsByType(applicableResults, selectedResultCalcType).filter(
        (result) => isApplicableSuccessResult(result),
      );
      const groupedCalcResults = groupByInstitution(filteredCalcResults);

      return groupedCalcResults.map((institutionResults) => {
        let children;

        if (institutionResults[0]?.insuranceInstitution.institutionEnum === InstitutionEnum.COLONNADE) {
          children = [
            {
              label: t("calc.vehicle.helpers.gapWithComplicityReinsurance"),
              value: true,
            },
            {
              label: t("calc.vehicle.helpers.gapWithoutComplicityReinsurance"),
              value: false,
            },
          ];
        } else if (institutionResults[0]?.insuranceInstitution.institutionEnum === InstitutionEnum.DEFEND) {
          children = resolveGapCoverageLimitOptions(
            toDate(calcData.clientsData.holderRegistrationDate) as Dayjs,
          ).map<DefaultOptionType>((coverageLimit) => ({
            label: `${t("calc.vehicle.results.coverageLimit")} ${t(
              "calc.vehicle.enums.gapCoverageLimit." + coverageLimit,
            )}`,
            value: coverageLimit,
          }));
        } else {
          children = [{ label: "", value: "" }];
        }

        return {
          label: institutionResults[0]?.insuranceInstitution.name,
          value: institutionResults[0]?.insuranceInstitution.institutionEnum,
          children: children,
        };
      });
    }

    return [];
  };

  const policyHolderIsCompany = policyHolderType === ClientType.SELF_EMPLOYED || policyHolderType === ClientType.LEGAL;
  const mtplDateFrom = toDate(calcData.generalData.effectiveBeginningDate);
  const mtplDateTo = mtplDateFrom?.add(1, "years").subtract(1, "days");

  return (
    <Card type="inner" className="card-box" title={t("calc.vehicle.sections.otherData")}>
      <Row gutter={rowGutter}>
        {(selectedResultInstitutionEnum === InstitutionEnum.GENERALI ||
          selectedResultInstitutionEnum === InstitutionEnum.KOMUNALNA ||
          selectedResultInstitutionEnum === InstitutionEnum.KOOPERATIVA) && (
          <Col span={colSpan}>
            <CityAutoComplete
              formItemProps={{
                name: ["generalData", "signCity"],
                label: t("calc.vehicle.attrs.generalData.signCity"),
                rules: [validations.notBlank, validations.size(1, 64), validations.pattern(regexPatterns.wordRegex)],
              }}
            />
          </Col>
        )}

        {selectedResultInstitutionEnum === InstitutionEnum.UNIQA &&
          calcData.clientsData.leasing &&
          (selectedResultCalcType === CalcType.CRASH || selectedResultCalcType === CalcType.MTPL_CRASH) && (
            <Col span={colSpan}>
              <Form.Item
                name={["generalData", "loanNumber"]}
                label={t("calc.vehicle.attrs.generalData.loanNumber")}
                rules={[validations.notBlank, validations.size(1, 64)]}
              >
                <Input />
              </Form.Item>
            </Col>
          )}
      </Row>

      {selectedResultInstitutionEnum === InstitutionEnum.ALLIANZ && selectedResultCalcType === CalcType.MTPL_CRASH && (
        <>
          <Divider className="divider-subheader">{t("calc.vehicle.helpers.mtplOtherData")}</Divider>
          <Row gutter={rowGutter}>
            <Col span={colSpan}>
              <Form.Item
                name={["generalData", "mtplInsuranceEffectiveBeginningDate"]}
                label={t("calc.vehicle.attrs.generalData.mtplInsuranceEffectiveBeginningDate")}
                rules={
                  mtplDateFrom && mtplDateTo
                    ? [validations.notNull, validations.dateInInterval(mtplDateFrom, mtplDateTo)]
                    : [validations.notNull]
                }
                {...datePickerFormItemProps}
              >
                <DatePicker {...datePickerStandardProps} minDate={mtplDateFrom} maxDate={mtplDateTo} />
              </Form.Item>
            </Col>
          </Row>
        </>
      )}

      {(selectedResultInstitutionEnum === InstitutionEnum.COLONNADE ||
        selectedResultInstitutionEnum === InstitutionEnum.DEFEND) &&
        selectedResultCalcType === CalcType.GAP && (
          <>
            <Divider className="divider-subheader">{t("calc.vehicle.sections.gapData")}</Divider>

            <Row gutter={rowGutter}>
              {selectedResultInstitutionEnum === InstitutionEnum.DEFEND && (
                <HiddenInput
                  name={["generalData", "gapComplicityReinsurance"]}
                  initialValue={!!(selectedResult.data as GapCalcResultData)?.options?.[0]?.complicityReinsurance}
                />
              )}
              {selectedResultInstitutionEnum === InstitutionEnum.COLONNADE && (
                <Col span={colSpan}>
                  <Form.Item
                    name={["generalData", "gapComplicityReinsurance"]}
                    className="form-item-without-label"
                    valuePropName="checked"
                    rules={[validations.none]}
                    initialValue={false}
                  >
                    <Checkbox>{t("calc.vehicle.attrs.generalData.gapComplicityReinsurance")}</Checkbox>
                  </Form.Item>
                </Col>
              )}
              <Col span={colSpan}>
                <Form.Item
                  name={["generalData", "gapPaymentFrequency"]}
                  label={t("calc.vehicle.helpers.gapPaymentFrequencyLabel")}
                  rules={[validations.notNull]}
                >
                  <Select
                    {...selectStandardProps}
                    options={[PaymentFrequency.ONCE, PaymentFrequency.ANNUALLY].map((frequency) => ({
                      value: frequency,
                      label: t("contract.enums.paymentFrequency." + frequency),
                    }))}
                  />
                </Form.Item>
              </Col>
              <Form.Item
                noStyle
                shouldUpdate={(prev, next) =>
                  prev.generalData.gapComplicityReinsurance !== next.generalData.gapComplicityReinsurance ||
                  prev.generalData.gapPaymentFrequency !== next.generalData.gapPaymentFrequency
                }
              >
                {({ getFieldValue }) => {
                  const { gapComplicityReinsurance, gapPaymentFrequency } = getFieldValue([
                    "generalData",
                  ]) as VehicleGenGeneralData;
                  const option = (selectedResult.data as GapCalcResultData).options?.find(
                    (option) =>
                      option.complicityReinsurance === gapComplicityReinsurance &&
                      option.paymentFrequency === gapPaymentFrequency,
                  );

                  return (
                    option && (
                      <Col span={colSpan * 3} className="form-item-without-label">
                        {t("calc.vehicle.results.annualPremium")}:{" "}
                        <b>
                          {isDefined(option.annualPremium) ? formatLocaleCurrency(option.annualPremium) : undefined}
                        </b>
                        {option.paymentFrequency === PaymentFrequency.ANNUALLY && (
                          <>
                            {" "}
                            | {t("calc.vehicle.helpers.gapAnnualPremiumSum")}:{" "}
                            <b>{formatLocaleCurrency(option.premium)}</b>
                          </>
                        )}
                      </Col>
                    )
                  );
                }}
              </Form.Item>
            </Row>
          </>
        )}

      {selectedResultInstitutionEnum === InstitutionEnum.CSOB && (
        <>
          <Divider className="divider-subheader">{t("calc.vehicle.sections.conflictOfInterestsStatement")}</Divider>

          <Row gutter={rowGutter}>
            <Col span={colSpan * 3}>
              <Form.Item
                name={["generalData", "agentIsClosePersonToClient"]}
                valuePropName="checked"
                rules={[validations.none]}
                initialValue={false}
              >
                <Checkbox>
                  <LabelWithTooltip
                    label={t("calc.vehicle.attrs.generalData.agentIsClosePersonToClient")}
                    tooltip={t("calc.vehicle.helpers.agentIsClosePersonToClientDesc")}
                  />
                </Checkbox>
              </Form.Item>
            </Col>
          </Row>
        </>
      )}

      {selectedResultInstitutionEnum === InstitutionEnum.UNIQA && calcData.reinsurancesData.uniqaSidecarMtpl && (
        <>
          <Divider className="divider-subheader">
            {t("calc.vehicle.attrs.reinsurancesData.uniqaSidecarMtplData._label")}
          </Divider>

          <Row gutter={rowGutter}>
            <Col span={colSpan}>
              <Form.Item
                name={["reinsurancesData", "uniqaSidecarMtplData", "licensePlate"]}
                label={t("calc.vehicle.attrs.reinsurancesData.uniqaSidecarMtplData.licensePlate")}
                rules={[validations.fullLicensePlate]}
                normalize={licensePlateNormalizeFunction}
              >
                <Input />
              </Form.Item>
            </Col>

            <Col span={colSpan}>
              <Form.Item
                name={["reinsurancesData", "uniqaSidecarMtplData", "vin"]}
                label={t("calc.vehicle.attrs.reinsurancesData.uniqaSidecarMtplData.vin")}
                rules={[validations.notBlank, validations.size(3, 17)]}
                normalize={upperCaseStringNormalizeFunction}
              >
                <Input />
              </Form.Item>
            </Col>

            <Col span={colSpan}>
              <Form.Item
                name={["reinsurancesData", "uniqaSidecarMtplData", "registrationCertificateNumber"]}
                label={
                  <LabelWithTooltip
                    label={t("calc.vehicle.attrs.reinsurancesData.uniqaSidecarMtplData.registrationCertificateNumber")}
                    tooltip={t("calc.vehicle.helpers.registrationCertificateNumberDesc")}
                  />
                }
                rules={[validations.pattern(regexPatterns.registrationCertificateNumberRegex), validations.length(8)]}
                normalize={upperCaseStringNormalizeFunction}
              >
                <Input />
              </Form.Item>
            </Col>

            <Col span={colSpan}>
              <Form.Item
                name={["reinsurancesData", "uniqaSidecarMtplData", "totalWeight"]}
                label={t("calc.vehicle.attrs.reinsurancesData.uniqaSidecarMtplData.totalWeight")}
                rules={[validations.notNull, validations.minNumber(1), validations.maxNumber(750)]}
              >
                <InputNumber addon="kg" />
              </Form.Item>
            </Col>

            <Col span={colSpan}>
              <Form.Item
                name={["reinsurancesData", "uniqaSidecarMtplData", "firstRegistrationYear"]}
                label={t("calc.vehicle.attrs.reinsurancesData.uniqaSidecarMtplData.firstRegistrationYear")}
                rules={[validations.notNull, validations.minNumber(1900), validations.maxNumber(dayjs().year())]}
              >
                <InputNumber />
              </Form.Item>
            </Col>
          </Row>
        </>
      )}

      <Divider className="divider-subheader">{t("calc.vehicle.sections.financialMediationData")}</Divider>

      <Row gutter={rowGutter}>
        <Col span={colSpan}>
          <Form.Item
            name={["financialMediationData", "clientExperience"]}
            label={t("calc.enums.clientExperience._label")}
            rules={[validations.notNull]}
            initialValue={policyHolderIsCompany ? ClientExperience.SUFFICIENT : undefined}
          >
            <Select
              {...selectStandardProps}
              disabled={policyHolderIsCompany}
              options={(policyHolderIsCompany
                ? [ClientExperience.SUFFICIENT]
                : [ClientExperience.NONE, ClientExperience.MINIMAL]
              ).map((experience) => ({
                value: experience,
                label: t("calc.enums.clientExperience." + experience),
              }))}
            />
          </Form.Item>
        </Col>

        <Col span={bigColSpan}>
          <Form.Item
            name={["financialMediationData", "otherClientRequirements"]}
            label={t("calc.vehicle.attrs.financialMediationData.otherClientRequirements")}
            rules={[validations.none]}
          >
            <Input />
          </Form.Item>
        </Col>
      </Row>

      {(selectedResultCalcType === CalcType.MTPL ||
        selectedResultCalcType === CalcType.CRASH ||
        selectedResultCalcType === CalcType.MTPL_CRASH ||
        selectedResultCalcType === CalcType.GAP) && (
        <Row gutter={rowGutter}>
          <Col span={bigColSpan}>
            <Form.Item
              name={["financialMediationData", "recommendedResult"]}
              label={t("calc.vehicle.attrs.financialMediationData.recommendedResult")}
              rules={[validations.notNull]}
            >
              <Cascader allowClear={false} expandTrigger="hover" options={getRecommendedResultOptions()} />
            </Form.Item>
          </Col>

          <Col span={12}>
            <Form.Item
              name={["financialMediationData", "recommendationReason"]}
              label={t("calc.vehicle.attrs.financialMediationData.recommendationReason")}
              rules={[validations.none]}
            >
              <Input />
            </Form.Item>
          </Col>
        </Row>
      )}

      <Row gutter={rowGutter}>
        <Col span={bigColSpan}>
          <Form.Item
            name={["financialMediationData", "additionalClientStatement"]}
            label={t("calc.vehicle.attrs.financialMediationData.additionalClientStatement")}
            rules={[validations.none]}
          >
            <Input />
          </Form.Item>
        </Col>

        <Col span={12}>
          <Form.Item
            name={["financialMediationData", "additionalSuitabilityStatement"]}
            label={
              <LabelWithTooltip
                label={t("calc.vehicle.attrs.financialMediationData.additionalSuitabilityStatement")}
                tooltip={t("calc.vehicle.helpers.additionalSuitabilityStatementDesc")}
              />
            }
            rules={[validations.none]}
          >
            <Input />
          </Form.Item>
        </Col>
      </Row>
    </Card>
  );
};
